// @ts-nocheck
import { AuthService } from "../../auth";
import { isLoginOrOtpCheckRequest } from "../utils";
import { LOGOUT_REQUEST_PATHNAME } from "./constants";
import { REFRESH_TOKEN_REQUEST_PATHNAME } from "../constants";
import {
  ContentType,
  RequestParams,
  QueryParamsType,
  FullRequestParams,
} from "./types";

function HttpClient() {
  const { fetch } = window;
  const baseUrl = process.env.MONOLITH_API_URL || "/ib6_new/rest-front/v2";
  const baseApiParams: RequestParams = {
    credentials: "include",
    headers: {},
    redirect: "follow",
    referrerPolicy: "no-referrer",
  };

  const addQueryParam = (query: QueryParamsType, key: string) => {
    const value = query[key];

    if (Array.isArray(value)) {
      return value
        .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
        .join("&");
    }

    return `${encodeURIComponent(key)}=${encodeURIComponent(
      typeof value === "number" ? value : value.toString()
    )}`;
  };

  /** Возможно понадобится в будущем. */
  // const addQueryParams = (rawQuery?: TQueryParamsType): string => {
  //   const queryString = this.toQueryString(rawQuery);
  //   return queryString ? `?${queryString}` : "";
  // }

  const toQueryString = (rawQuery?: QueryParamsType): string => {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter(
      (key) => typeof query[key] !== "undefined"
    );

    const mappedQueries = keys.map((key) =>
      typeof query[key] === "object" && !Array.isArray(query[key])
        ? toQueryString(query[key] as QueryParamsType)
        : addQueryParam(query, key)
    );

    return mappedQueries.join("&");
  };

  const contentFormatters: Record<ContentType, (input: any) => any> = {
    [ContentType.JSON]: (input: any) =>
      !!input && (typeof input === "object" || typeof input === "string")
        ? JSON.stringify(input)
        : input,
    [ContentType.FORM_DATA]: (input: any) =>
      Object.keys(input || {}).reduce((data, key) => {
        data.append(key, input[key]);
        return data;
      }, new FormData()),
    [ContentType.URL_ENCODED]: (input: any) => toQueryString(input),
  };

  const mergeRequestParams = (
    params1: RequestParams,
    params2?: RequestParams
  ): RequestParams => ({
    ...baseApiParams,
    ...params1,
    ...(params2 || {}),
    headers: {
      ...(baseApiParams.headers || {}),
      ...(params1.headers || {}),
      ...((params2 && params2.headers) || {}),
      ...(AuthService.hasAccessToken
        ? { Authorization: `Bearer ${AuthService.getAccessToken()}` }
        : {}),
    },
  });

  this.request = async ({
    body,
    path,
    type,
    query,
    ...params
  }: FullRequestParams): Promise<any> => {
    /**
     * INFO: Отменяем все запросы в очереди, если один из запросов получил ответ с кодом 401
     * и установил значение свойства AuthService.isNeedToCancelRequestsInQueue = true.
     */
    if (AuthService.isNeedToCancelRequestsInQueue) {
      return Promise.reject(new Error(`Request to ${path} was cancelled`));
    }

    try {
      if (
        AuthService.needUpdateAuthData &&
        path !== REFRESH_TOKEN_REQUEST_PATHNAME
      ) {
        await AuthService.updateAuthData();
      }
    } catch (error) {
      /**
       * TODO: Необходимо уточнить у дизайнеров как обрабатывать ошибку обновления токена на UI (http://sdlc.go.rshbank.ru/jira/browse/OB_COREUI-103).
       * Сейчас в случае любой ошибки (клиентской / серверной) мы редиректим пользователя на страницу логина, что может вызвать ложное представление о том что он разлогинен, хотя это не всегда так.
       */
      AuthService.resetAuthData();
      AuthService.redirectToLoginPage();

      /**
       * TODO: Обработку этого reject'а необходимо производить на уровне проекта и в случае 500-х ответов сервера отображать пользователю ErrorPage-компонент (http://sdlc.go.rshbank.ru/jira/browse/OB_COREUI-101).
       * Однако пока не уточним вопрос указанный в предыдущем комментарии, обработка этого reject'а не имеет смысла, поскольку сейчас при любой ошибке происходит редирект на страницу логина.
       */
      return Promise.reject(error);
    }

    const requestParams = mergeRequestParams(params);
    const queryString = query && toQueryString(query);
    const payloadFormatter = contentFormatters[type || ContentType.JSON];

    const response = await fetch(
      `${params?.baseUrl || baseUrl}${path}${
        queryString ? `?${queryString}` : ""
      }`,
      {
        ...requestParams,
        headers: {
          ...(type && type !== ContentType.FORM_DATA
            ? { "Content-Type": type }
            : {}),
          ...(requestParams.headers || {}),
        },
        body: body ? payloadFormatter(body) : null,
      }
    );

    if (response.status === 401) {
      AuthService.setLoginPageNextParam(window.location.pathname);
      AuthService.resetAuthData();
      AuthService.redirectToLoginPage();
    }

    // Нам необходимо минимизировать любые манипуляции с токенами в проектах, поэтому инкапсулируем всю логику работы с ними на уровне http-клиента и auth-сервиса.
    if (isLoginOrOtpCheckRequest(path) && response.ok) {
      const clonedLoginResponse = response.clone();
      const loginData = await clonedLoginResponse.json();

      if (loginData.jwt) {
        await AuthService.setAccessToken(loginData.jwt);
      }
    }

    if (path === LOGOUT_REQUEST_PATHNAME && response.ok) {
      AuthService.resetAuthData();
      AuthService.redirectToLoginPage();
    }

    return response;
  };
}

const http = new HttpClient();

const POST = (
  path,
  body = null,
  query = null,
  type = ContentType.JSON,
  params = {}
): Promise<any> =>
  http.request({
    path,
    method: "POST",
    body,
    query,
    type,
    ...params,
  });

const GET = (path, query = null, signal = null, params = {}): Promise<any> =>
  http.request({
    path,
    method: "GET",
    query,
    signal,
    ...params,
  });

const PUT = (path, body, type = ContentType.JSON): Promise<any> =>
  http.request({
    path,
    method: "PUT",
    body,
    type,
  });

const DELETE = (path, query = null): Promise<any> =>
  http.request({
    path,
    method: "DELETE",
    query,
  });

export { GET, POST, PUT, DELETE };
